/**
 * Post-login redirect hook. Thin React wrapper over the pure
 * `resolveLandingRoute` helper in `src/lib/post-login-resolver.ts`.
 *
 * Also exports the redirect-loop guard used by callers who navigate to the
 * resolved destination.
 */
import { useAuth } from "@/contexts/AuthContext";
import { useAccountCapabilities } from "@/hooks/useAccountCapabilities";
import { resolveLandingRoute } from "@/lib/post-login-resolver";
import { testRoleLandingRoute } from "@/lib/test-accounts";

const DOMAIN_KEY = "vitolier_last_domain";
const LOOP_KEY = "vitolier_redirect_trail";
const LOOP_WINDOW_MS = 10_000;
const LOOP_MAX_HOPS = 3;

export type Domain = "consumer" | "trade";

export function getLastDomain(): Domain {
  const stored = typeof window !== "undefined" ? localStorage.getItem(DOMAIN_KEY) : null;
  return stored === "trade" ? "trade" : "consumer";
}

export function setLastDomain(domain: Domain) {
  if (typeof window !== "undefined") localStorage.setItem(DOMAIN_KEY, domain);
}

function readTrail(): Array<{ path: string; t: number }> {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(LOOP_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<{ path: string; t: number }>;
    const now = Date.now();
    return parsed.filter((h) => now - h.t < LOOP_WINDOW_MS);
  } catch {
    return [];
  }
}

export function recordRedirectHop(path: string) {
  if (typeof window === "undefined") return;
  const trail = readTrail();
  trail.push({ path, t: Date.now() });
  sessionStorage.setItem(LOOP_KEY, JSON.stringify(trail.slice(-10)));
}

export function clearRedirectTrail() {
  if (typeof window !== "undefined") sessionStorage.removeItem(LOOP_KEY);
}

function wouldLoop(path: string): boolean {
  const trail = readTrail();
  const hits = trail.filter((h) => h.path === path).length;
  return hits >= LOOP_MAX_HOPS;
}

/**
 * Returns the canonical post-login destination for the current session.
 * Delegates all decision logic to `resolveLandingRoute`.
 */
export function usePostLoginRedirect(): string {
  const { isBusiness, isAdmin, isConsumer, roles } = useAuth();
  const { capabilities, isLoading } = useAccountCapabilities();

  if (isLoading) {
    // Fall back to legacy role booleans during capability resolution so the
    // very first render doesn't force everyone through /account/setup.
    const hasBusinessAccess = isBusiness || isAdmin;
    const hasConsumerAccess = isConsumer;
    if (hasBusinessAccess && hasConsumerAccess) {
      return getLastDomain() === "trade" ? "/trade/dashboard" : "/app/home";
    }
    if (hasBusinessAccess) return "/trade/dashboard";
    return "/app/home";
  }

  // QA test roles: when the account has no production context (no consumer
  // profile, no trade memberships, not staff), land on the mirrored dashboard
  // for the test role instead of the generic account-setup fallback.
  const testRoute = testRoleLandingRoute(roles);
  if (
    testRoute &&
    capabilities.tradeMemberships.length === 0 &&
    !capabilities.hasConsumerProfile &&
    !capabilities.isStaff
  ) {
    return testRoute;
  }

  return resolveLandingRoute(capabilities, {
    lastDomain: getLastDomain(),
    wouldLoop,
  });
}
